<template>
<div class="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
  <div class="sm:mx-auto sm:w-full sm:max-w-md">
    <h2 class="mt-4 text-center text-3xl font-bold text-gray-900">
      Order complete
    </h2>
  </div>
  <div class="mt-8 mx-auto w-full max-w-2xl">
    <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
      <div v-if="validateMembershipState.result && validateMembershipState.result.record">
        <div v-if="eventId === '138'">
          <p class="mt-2 font-bold">Thank you for your order to attend Battle for Britain 2026.</p>
          <p class="mt-2">You will receive an email asking you to CONFIRM your ticket details. Please kindly confirm the details as requested.</p>
          <p class="mt-2"><em>If you do not receive the email in the next few hours, then please check your junk mail. Failing that, please contact <strong><span>tech</span>@prayerstorm.org</strong> for assistance.</em></p>

          <div v-if="campaign" class="mt-8">
            <h2 class="text-2xl font-bold text-gray-900">Support this conference</h2>
            <p class="mt-2">In order to keep ticket costs at a reasonable level, we ask that you consider supporting Battle for Britain 2026. We aim to fund this amazing conference opportunity with your generous support and minimal ticket pricing to make if as affordable as possible for all to attend.</p>

            <div class="mt-4 w-full">
            <div class="flex justify-between pb-4">
              <a class="font-bold text-lg hover:underline" :href="'/support/5/checkout/' + validateMembershipState.sessionId">
                {{ campaign.title }}
              </a>
              <div class="flex-none pt-2">
                <a class="bg-blue hover:bg-blue-dark rounded-md px-6 py-3 text-md font-semibold text-white" :href="'/support/5/checkout/' + validateMembershipState.sessionId">
                  Donate Now
                </a>
              </div>
            </div>
            <div class="overflow-hidden rounded-full bg-gray-200 mt-2 lg:mt-4">
              <div class="h-2 rounded-full bg-blue" :style="{ width: progressPercent + '%' }" />
            </div>
            <div class="flex justify-between my-2 lg:my-4">
              <div class="mr-6">
                <div class="text-lg font-bold">{{ raisedDisplay }}</div>
                <div>Raised</div>
              </div>
              <div>
                <div class="text-lg font-bold">{{ targetDisplay }}</div>
                <div class="text-right">Total Goal</div>
              </div>
            </div>
            <p class="mt-2"><em>** Periodically updates</em></p>
          </div>
          </div>

        </div>
        <div v-else>
          <p>Thank you, your order was successful.</p>
          <p class="mt-2">If you ordered an <span class="font-bold">online membership or online eTicket</span>, this should now be active on your account.</p>
          <p class="mt-2">If you ordered <span class="font-bold">tickets to attend an in-person event</span>, then you will receive an email asking you to CONFIRM your ticket details. Please kindly confirm the details as requested. <em>If you do not receive the email in the next few hours, then please check your junk mail. Failing that, please contact tech@prayerstorm.org for assistance.</em></p>
          <router-link :to="redirectUrl" class="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue">
            Continue
          </router-link>
        </div>
      </div>
      <div v-else>
        <p>Validating. Please wait...</p>
        <ErrorMessage :formError="validateMembershipState.error"></ErrorMessage>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useCampaigns } from '@/composables/useCampaigns'
import { useMemberships } from '@/composables/useMemberships'
import ErrorMessage from '@/components/ErrorMessage.vue'

const CAMPAIGN_ID = 'mx4n8g0a75c5l6l'

export default defineComponent({
  name: 'CheckoutSuccess',
  components: {
    ErrorMessage
  },
  setup () {
    const route = useRoute()
    const { getCampaignById } = useCampaigns()
    const { validateMembershipState, validateMembership } = useMemberships()
    const campaignState = getCampaignById(CAMPAIGN_ID)
    const campaign = computed(() => campaignState.result)
    const eventId = ref(route.params.eventId)
    if (eventId.value === '138') {
      if (window.gtag) {
        window.gtag('event', 'metric_track', {
          metric_name: 'event_order_138'
        })
      }
    }
    // set the sessionId
    validateMembershipState.sessionId = route.params.sessionId

    const redirectUrl = route.query.return ? route.query.return : '/'

    const toMajorCurrency = (price) => {
      const value = Number(price)
      if (!Number.isFinite(value)) {
        return 0
      }

      return value / 100
    }

    const getCurrencyCode = (currencyCode) => {
      const normalized = String(currencyCode || '').toUpperCase()
      if (normalized === 'GBP' || normalized === 'GPB') {
        return 'GBP'
      }

      return normalized || 'GBP'
    }

    const formatMoney = (amount, currencyCode) => {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: getCurrencyCode(currencyCode),
        maximumFractionDigits: 0
      }).format(amount)
    }

    const raisedAmount = computed(() => {
      return toMajorCurrency(campaign.value?.expand?.raised?.price)
    })

    const targetAmount = computed(() => {
      return toMajorCurrency(campaign.value?.expand?.target?.price)
    })

    const raisedDisplay = computed(() => {
      return formatMoney(raisedAmount.value, campaign.value?.expand?.raised?.currency)
    })

    const targetDisplay = computed(() => {
      return formatMoney(targetAmount.value, campaign.value?.expand?.target?.currency)
    })

    const progressPercent = computed(() => {
      if (!targetAmount.value) {
        return 0
      }

      const ratio = (raisedAmount.value / targetAmount.value) * 100
      return Math.max(0, Math.min(100, Math.round(ratio)))
    })

    onMounted(() => {
      validateMembership()
        .then(() => {
        }).catch(() => {
        })
    })

    return {
      eventId,
      campaign,
      raisedDisplay,
      targetDisplay,
      progressPercent,
      validateMembershipState,
      redirectUrl
    }
  }
})
</script>
